import { useParams } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, ListItemIcon, Menu, MenuItem, Stack, Typography, useMediaQuery } from '@mui/material';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { BackButtonLink } from '../../../presentation-components/display-elements-components/back-button-link';
import { ReportingGhgTable } from '../../../presentation-components/reporting-components/ghg/reporting-ghg-table';
import GeneratedReportService from '../../../../shared/services/generated-report/generatedReport.service';
import { IGhgTable } from '../../../../shared/services/generated-report/generated-report.model';
import { DownloadCSV } from '../../../presentation-components/miscellaneous-components/download-csv';
import { AutoAwesomeOutlined, DownloadOutlined, PictureAsPdfOutlined } from '@mui/icons-material';
import gaiaTheme from '../../../../root-components/theme';
import React from 'react';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import AiCopilotService from '../../../../shared/services/ai-copilot/ai-copilot.service';
import { DraftReportSectionType } from '../../../../shared/services/ai-copilot/ai-copilot.model';
import { CopilotPanel } from '../../../copilot/CopilotPanel';
import { downloadBlob } from '../../../../shared/utils/download-file-utils';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service';
import { FormTextInputDisplay } from '../../../presentation-components/form-field-components/new-and-edit/form-text-input';
import { userHasRequiredRole } from '../../../../shared/utils/role-utils';
import { toPlainCommentary } from '../../../../shared/utils/ai-text-utils';

const COMMENTARY_FIELD = 'performanceCommentary';
const COMMENTARY_MAX = 5000;

export const ReportingGhgReportDetailPage = () => {
  const { reportPeriodId } = useParams();
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userDetailsContext = useContext(UserDetailsContext);
  const generatedReportService = useMemo(() => new GeneratedReportService(), []);
  const aiCopilotService = useMemo(() => new AiCopilotService(), []);
  const [tableData, setTableData] = useState<IGhgTable>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [copilotSection, setCopilotSection] = useState<DraftReportSectionType | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const userNotificationsContext = useContext(UserNotificationsContext);
  const openDropdown = Boolean(anchorEl);
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  const reportPeriodService = useMemo(() => new ReportPeriodService(), []);
  const [commentaryEditField, setCommentaryEditField] = useState<string>('');
  const [commentaryDraft, setCommentaryDraft] = useState<string | null>(null);
  const [savingCommentary, setSavingCommentary] = useState(false);

  const reportPeriod = reportPeriodContext.allReportPeriods.find((rp) => rp.id === reportPeriodId);
  const friendlyName = reportPeriod?.friendlyName ?? '';
  const canUseCopilot = !!userDetailsContext.userDetails?.organisation?.aiCopilotEnabled;

  // Commentary is part of the report, so a locked period shows it read-only (or not at all when
  // blank); reopen the period to change it. Admins and analysts write it.
  const isPeriodLocked = !!reportPeriod?.locked;
  const canEditCommentary = !isPeriodLocked && userHasRequiredRole(userDetailsContext.userDetails, ['admin', 'analyst']);
  const savedCommentary = tableData?.performanceCommentary ?? '';
  const isEditingCommentary = commentaryEditField === COMMENTARY_FIELD;
  const commentaryValue = isEditingCommentary ? (commentaryDraft ?? savedCommentary) : savedCommentary;
  const showCommentary = !!tableData && !(isPeriodLocked && !savedCommentary);
  const commentaryTooLong = commentaryValue.length > COMMENTARY_MAX;

  const cancelCommentaryHandler = () => {
    setCommentaryEditField('');
    setCommentaryDraft(null);
  };

  const saveCommentaryHandler = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!reportPeriodId || !isEditingCommentary || commentaryTooLong || savingCommentary) return;
    setSavingCommentary(true);
    try {
      const response = await reportPeriodService.updatePerformanceCommentary(reportPeriodId, commentaryValue.trim() || null);
      setTableData((current) => (current ? { ...current, performanceCommentary: response.performanceCommentary } : current));
      cancelCommentaryHandler();
      userNotificationsContext.openSnackbar({ open: true, message: 'Commentary saved' });
    } catch (err: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'The commentary could not be saved, please try again',
        backgroundColor: 'text.primary',
      });
    } finally {
      setSavingCommentary(false);
    }
  };

  // "Use as commentary" on a G.AI draft: the text goes into the field in edit state. Nothing is
  // saved until the user presses save.
  const useDraftAsCommentaryHandler = (text: string) => {
    setCommentaryDraft(toPlainCommentary(text));
    setCommentaryEditField(COMMENTARY_FIELD);
    setCopilotSection(null);
  };

  useEffect(() => {
    if (!reportPeriodId) return;
    generatedReportService
      .getGhg(reportPeriodId)
      .then((response) => setTableData(response))
      .catch((err) => console.log(err));
  }, [reportPeriodId, generatedReportService]);

  const handleDownloadPdf = () => {
    if (!reportPeriodId || downloadingPdf) return;
    setDownloadingPdf(true);
    setAnchorEl(null);
    generatedReportService.getGhgPdf(reportPeriodId)
      .then((blob) => downloadBlob({ blob, fileName: `${`GHG Report ${friendlyName}`.trim() || 'GHG Report'}.pdf` }))
      .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'The report could not be prepared, please try again', backgroundColor: 'text.primary' }))
      .finally(() => setDownloadingPdf(false));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pt: 4,
        pb: isMobileModeSm ? 3 : 6,
        pl: isMobileModeSm ? 3 : 6,
        pr: isMobileModeSm ? 3 : 6,
        mt: isMobileModeSm ? 5 : 0,
        maxWidth: '100%',
      }}
    >
      <Stack display="flex" justifyContent="flex-start" sx={{ mb: 2 }}>
        <BackButtonLink buttonText="Back to GHG reporting" link="/reporting/greenhouse-gas-emissions-report" />
        <Stack
          direction={isMobileModeMd ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isMobileModeMd ? 'flex-start' : 'center'}
          sx={{ mb: 4 }}
        >
          {tableData && (
            <Typography variant="h4" sx={{ color: 'text.primary', mb: isMobileModeMd ? 1 : 0 }}>
              Regulatory Greenhouse Gas Emissions Report ({friendlyName})
            </Typography>
          )}
          <Stack flexDirection="row" sx={{ justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <Stack display="flex" flexDirection="row" alignItems="center">
              {tableData && (
                <Box sx={{ position: 'relative', mr: 2 }}>
                  {canUseCopilot && reportPeriodId && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<AutoAwesomeOutlined fontSize="small" />}
                        onClick={() => setCopilotSection('performance_summary')}
                        sx={{ mr: 1 }}
                      >
                        Draft performance summary
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<AutoAwesomeOutlined fontSize="small" />}
                        onClick={() => setCopilotSection('methodology')}
                        sx={{ mr: 1 }}
                      >
                        Draft methodology wording
                      </Button>
                    </>
                  )}
                  <Button
                    id="ghg-download-button"
                    variant="text"
                    aria-controls={openDropdown ? 'ghg-download-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={openDropdown ? 'true' : undefined}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{ borderStyle: 'solid', borderWidth: '1px', borderColor: 'primary.main' }}
                  >
                    <DownloadOutlined sx={{ color: 'primary.main' }} />
                    <Typography sx={{ color: 'primary.main', display: 'inline' }}>Download</Typography>
                  </Button>
                  <Menu
                    id="ghg-download-menu"
                    anchorEl={anchorEl}
                    open={openDropdown}
                    onClose={() => setAnchorEl(null)}
                    MenuListProps={{ 'aria-labelledby': 'ghg-download-button' }}
                  >
                    <MenuItem disabled={downloadingPdf} onClick={handleDownloadPdf}>
                      <ListItemIcon><PictureAsPdfOutlined fontSize='small' /></ListItemIcon>
                      {downloadingPdf ? 'Preparing PDF\u2026' : 'PDF (.pdf)'}
                    </MenuItem>
                    <DownloadCSV
                      data={tableData}
                      fileName={`GHG Report ${friendlyName}`.trim() || 'GHG Report'}
                      fileType="ghg"
                    />
                  </Menu>
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      {reportPeriodId && showCommentary && (
        <Box component="form" onSubmit={saveCommentaryHandler} sx={{ mb: 3, maxWidth: '1100px' }}>
          <FormTextInputDisplay
            isEditPage={true}
            allowEdit={canEditCommentary && !savingCommentary}
            editField={commentaryEditField}
            setEditField={setCommentaryEditField}
            disabled={!isEditingCommentary || savingCommentary}
            title="Commentary"
            name={COMMENTARY_FIELD}
            value={commentaryValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCommentaryDraft(e.target.value)}
            cancelEditHandler={cancelCommentaryHandler}
            multiline={true}
            rows={4}
            isTopRow={true}
            isBottomRow={true}
            placeholder="What moved this year and why"
            customErrorMessage={commentaryTooLong ? `The commentary can be at most ${COMMENTARY_MAX.toLocaleString('en-GB')} characters.` : undefined}
            helperText={isPeriodLocked
              ? 'This period is locked. Reopen it to change the commentary.'
              : isEditingCommentary && commentaryValue.length > 4500
                ? `${commentaryValue.length.toLocaleString('en-GB')} of ${COMMENTARY_MAX.toLocaleString('en-GB')} characters`
                : 'Printed first in this report and its PDF.'}
          />
        </Box>
      )}
      {reportPeriodId && tableData && <ReportingGhgTable tableData={tableData} />}
      {canUseCopilot && reportPeriodId && copilotSection && (
        <CopilotPanel
          open={!!copilotSection}
          title={copilotSection === 'methodology' ? 'Draft methodology wording' : 'Draft performance summary'}
          onClose={() => setCopilotSection(null)}
          request={() => aiCopilotService.draftReportSection({
            reportPeriodId,
            sectionType: copilotSection as DraftReportSectionType,
          })}
          onUseDraft={copilotSection === 'performance_summary' && canEditCommentary ? useDraftAsCommentaryHandler : undefined}
        />
      )}
    </Box>
  );
};
